import { ref, watchEffect, getCurrentInstance, reactive, toRefs, watch, isRef, computed, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { toValue, createSharedComposable, useWindowSize, useBreakpoints, tryOnMounted, tryOnScopeDispose } from '@vueuse/core';

const context = ref({});
const breadcrumbs = ref([]);
const useRouteBreadcrumbs = (ctx = {}, options = {}) => {
  const {
    router = useRouter(),
    route: currentRoute = useRoute()
  } = options;
  if (ctx) {
    context.value = toValue(ctx);
  }
  if (!router) {
    console.warn("[RouteBreadcrumbs warn]: No router instance found in vue context.");
    return { breadcrumbs, context };
  }
  const deep = (breadcrumb, route) => {
    let to = router.resolve(route);
    let title = null;
    if (typeof breadcrumb === "function") {
      breadcrumb = breadcrumb.call(
        getCurrentInstance(),
        to,
        // to
        currentRoute,
        // from
        context.value
        // context
      );
    }
    if (Array.isArray(breadcrumb)) {
      return breadcrumb.reduce((acc, item) => {
        return [...deep(item, to), ...acc];
      }, []);
    }
    if (typeof breadcrumb === "object") {
      title = breadcrumb.label;
      to = router.resolve(breadcrumb.to);
    }
    if (typeof breadcrumb === "string") {
      title = breadcrumb;
    }
    return [{ title, route: to }];
  };
  const prune = (route, breads = []) => {
    const { breadcrumb } = route.meta || {};
    if (breadcrumb) {
      breads.push(...deep(breadcrumb, route).filter((b) => b.title));
    }
    const parent = route.matched && route.matched[route.matched.length - 2];
    if (parent) {
      return prune(router.resolve(parent), breads);
    }
    return breads.reverse();
  };
  watchEffect(() => {
    breadcrumbs.value = prune(currentRoute);
  });
  return { breadcrumbs, context };
};

const state$3 = reactive({
  title: null,
  message: null,
  payload: null,
  opened: false
});
const useNotifier = () => {
  const open = ({ title, message, payload } = {}) => {
    state$3.title = title;
    state$3.message = message;
    state$3.payload = payload;
    state$3.opened = true;
  };
  const close = () => {
    state$3.title = null;
    state$3.message = null;
    state$3.payload = null;
    state$3.opened = false;
  };
  return {
    open,
    close,
    ...toRefs(state$3)
  };
};

const state$2 = reactive({
  message: null,
  active: false,
  duration: 1e3 * 10,
  closable: false,
  dense: false,
  color: null,
  type: "warning"
});
const useToaster = () => {
  const trigger = (params = {}) => {
    state$2.active = true;
    Object.keys(params).forEach((key) => {
      state$2[key] = params[key];
    });
  };
  return {
    trigger,
    ...toRefs(state$2)
  };
};

let $window = typeof window !== "undefined" ? window : null;
function mockWindow(self) {
  $window = self || window;
}
const state$1 = reactive({
  networks: {
    baidu: "http://cang.baidu.com/do/add?iu=@u&it=@t",
    buffer: "https://bufferapp.com/add?text=@t&url=@u",
    email: "mailto:?subject=@t&body=@u%0D%0A@d",
    evernote: "https://www.evernote.com/clip.action?url=@u&title=@t",
    facebook: "https://www.facebook.com/sharer/sharer.php?u=@u&title=@t&description=@d&quote=@q&hashtag=@h",
    flipboard: "https://share.flipboard.com/bookmarklet/popout?v=2&url=@u&title=@t",
    hackernews: "https://news.ycombinator.com/submitlink?u=@u&t=@t",
    instapaper: "http://www.instapaper.com/edit?url=@u&title=@t&description=@d",
    line: "http://line.me/R/msg/text/?@t%0D%0A@u%0D%0A@d",
    linkedin: "https://www.linkedin.com/sharing/share-offsite/?url=@u",
    odnoklassniki: "https://connect.ok.ru/dk?st.cmd=WidgetSharePreview&st.shareUrl=@u&st.comments=@t",
    pinterest: "https://pinterest.com/pin/create/button/?url=@u&media=@m&description=@t",
    pocket: "https://getpocket.com/save?url=@u&title=@t",
    quora: "https://www.quora.com/share?url=@u&title=@t",
    reddit: "https://www.reddit.com/submit?url=@u&title=@t",
    skype: "https://web.skype.com/share?url=@t%0D%0A@u%0D%0A@d",
    sms: "sms:?body=@t%0D%0A@u%0D%0A@d",
    stumbleupon: "https://www.stumbleupon.com/submit?url=@u&title=@t",
    telegram: "https://t.me/share/url?url=@u&text=@t%0D%0A@d",
    tumblr: "https://www.tumblr.com/share/link?url=@u&name=@t&description=@d",
    twitter: "https://twitter.com/intent/tweet?text=@t&url=@u&hashtags=@h@tu",
    viber: "viber://forward?text=@t%0D%0A@u%0D%0A@d",
    vk: "https://vk.com/share.php?url=@u&title=@t&description=@d&image=@m&noparse=true",
    weibo: "http://service.weibo.com/share/share.php?url=@u&title=@t&pic=@m",
    whatsapp: "https://api.whatsapp.com/send?text=@t%0D%0A@u%0D%0A@d",
    wordpress: "https://wordpress.com/press-this.php?u=@u&t=@t&s=@d&i=@m",
    xing: "https://www.xing.com/social/share/spi?op=share&url=@u&title=@t",
    yammer: "https://www.yammer.com/messages/new?login=true&status=@t%0D%0A@u%0D%0A@d"
  }
});
const useNetworks = () => {
  const setNetwork = (key, value) => {
    state$1.networks = { ...state$1.networks, [key]: value };
  };
  return {
    setNetwork,
    ...toRefs(state$1)
  };
};
const usePopup = (options = { width: 626, height: 436 }) => {
  let popupInterval = null;
  let popupWindow = null;
  const width = $window.innerWidth || (document.documentElement.clientWidth || $window.screenX);
  const height = $window.innerHeight || (document.documentElement.clientHeight || $window.screenY);
  const systemZoom = width / $window.screen.availWidth;
  const popupLeft = (width - options.width) / 2 / systemZoom + ($window.screenLeft !== void 0 ? $window.screenLeft : $window.screenX);
  const popupTop = (height - options.height) / 2 / systemZoom + ($window.screenTop !== void 0 ? $window.screenTop : $window.screenY);
  const share = (shareLink, key) => {
    if (popupWindow && popupInterval) {
      clearInterval(popupInterval);
      popupWindow.close();
    }
    popupWindow = $window.open(
      shareLink,
      "sharer-" + key,
      ",height=" + options.height + ",width=" + options.width + ",left=" + popupLeft + ",top=" + popupTop + ",screenX=" + popupLeft + ",screenY=" + popupTop
    );
    if (!popupWindow)
      return;
    popupWindow.focus();
    popupInterval = setInterval(() => {
      if (!popupWindow || popupWindow.closed) {
        clearInterval(popupInterval);
        popupWindow = null;
      }
    }, 500);
  };
  const touch = (shareLink) => {
    $window.open(shareLink, "_blank");
  };
  return { share, touch };
};

const state = reactive({
  opened: false
});
const useSharer = () => {
  const open = () => {
    state.opened = true;
  };
  return {
    open,
    ...toRefs(state)
  };
};

const responsive = reactive({
  mobile: false,
  sm: false,
  sme: false,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
  xxxl: false,
  dimension: {
    width: 0,
    height: 0
  }
});
const useResponsive = createSharedComposable(() => {
  const { width, height } = useWindowSize();
  const b = useBreakpoints({
    xs: 390,
    sm: 768,
    md: 960,
    lg: 1200,
    xl: 1280,
    "2xl": 1280
  });
  watch([width, height], () => {
    responsive.mobile = b.smallerOrEqual("xs");
    responsive.sm = b.smallerOrEqual("sm");
    responsive.md = b.smallerOrEqual("md");
    responsive.lg = b.smallerOrEqual("lg");
    responsive.xl = b.smallerOrEqual("xl");
    responsive.xxl = b.smallerOrEqual("2xl");
    responsive.dimension.width = width.value;
    responsive.dimension.height = height.value;
  }, { immediate: true });
  return responsive;
});

const turnstileSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const turnstileLoadFunction = "cfTurnstileOnload";
var TurnstileState = /* @__PURE__ */ ((TurnstileState2) => {
  TurnstileState2["ready"] = "ready";
  TurnstileState2["unloaded"] = "unloaded";
  TurnstileState2["loading"] = "loading";
  return TurnstileState2;
})(TurnstileState || {});
const useTurnstile = (el, options) => {
  if (!options.sitekey) {
    throw new Error("Missing sitekey in useTurnstile options");
  }
  const resetTimeout = ref(null);
  const token = ref(null);
  const target = isRef(el) ? el : ref(el);
  const turnstileState = ref(
    window.turnstile !== void 0 ? "ready" /* ready */ : "unloaded" /* unloaded */
  );
  const turnstileOptions = computed(() => ({
    sitekey: options.sitekey,
    resetInterval: options.resetInterval,
    size: options.size || "normal",
    theme: options.theme || "light"
  }));
  let turnstileLoad;
  const turnstileLoadPromise = new Promise((resolve, reject) => {
    turnstileLoad = {
      resolve,
      reject
    };
    if (turnstileState.value === "ready" /* ready */) {
      resolve(void 0);
    }
  });
  const reset = () => {
    if (window.turnstile) {
      token.value = null;
      window.turnstile.reset();
    }
  };
  const remove = () => {
    if (window.turnstile) {
      token.value = null;
      window.turnstile.remove();
    }
  };
  const ensureTurnstile = () => {
    if (turnstileState.value === "unloaded" /* unloaded */) {
      turnstileState.value = "loading" /* loading */;
      const url = new URL(turnstileSrc);
      url.searchParams.append("onload", turnstileLoadFunction);
      if (options.explicit) {
        url.searchParams.append("render", "explicit");
      }
      const script = document.createElement("script");
      script.src = url.toString();
      script.async = true;
      script.addEventListener("error", () => {
        turnstileLoad.reject("Failed to load turnstile");
      });
      document.head.appendChild(script);
    }
    return turnstileLoadPromise;
  };
  const startResetTimeout = () => {
    resetTimeout.value = setTimeout(() => {
      reset();
    }, turnstileOptions.value.resetInterval || 3e5);
  };
  const callback = (t) => {
    if (!t) {
      startResetTimeout();
      return;
    }
    token.value = t;
    if (options.callback) {
      options.callback(t);
    }
    if (resetTimeout.value) {
      clearTimeout(resetTimeout.value);
    }
  };
  const render = () => {
    target.value && window.turnstile.render(target.value, {
      ...turnstileOptions.value,
      callback
    });
  };
  tryOnMounted(async () => {
    window[turnstileLoadFunction] = () => {
      turnstileState.value = "ready" /* ready */;
      turnstileLoad.resolve(void 0);
    };
    await ensureTurnstile();
    if (options.renderOnMount) {
      nextTick(render);
    }
  });
  tryOnScopeDispose(() => {
    remove();
    if (resetTimeout.value) {
      clearTimeout(resetTimeout.value);
    }
  });
  return {
    resetTimeout,
    turnstileState,
    token,
    reset,
    render,
    remove
  };
};

export { TurnstileState, mockWindow, useNetworks, useNotifier, usePopup, useResponsive, useRouteBreadcrumbs, useSharer, useToaster, useTurnstile };
